import { consumableHeal, itemDescription, type Ability, type ItemRarity, type Rules } from "odyssey-shared";
import type { GlyphName } from "../../ui/kit/Glyph.tsx";
import { RARITY_PIPS } from "../../ui/kit/ItemSlot.tsx";
import { durationText } from "../bar/units.ts";
import { menuHeading } from "../bag/describe.ts";
import { say } from "../../words.ts";

/**
 * The item card's content (P2.8-T46, ADR-125 decision 5; the design's `ItemCard` in `ItemSurfaces.jsx`, a visual
 * reference), drafted from the rules tables so no number is written into the page. The card shows only what an item
 * has today (decision 5.4): its rarity, name, kind, what a consumable restores or shortens and its use's cooldown, and
 * its description.
 * Condition, inscription, requirements and a price wait for the systems that give an item them. Pure: the page builds
 * a card only when one opens. Every name and description is carried as text and drawn as text, never as markup.
 */

/**
 * The headline's unit for a shorten (ADR-091's note for P2.8-T57, item 7, a draft): the effect's id with a capital and
 * "time", "Poison time".
 *
 * @param effect The effect's id.
 * @returns The unit.
 * @example
 * shortenUnit("poison");
 * // "Poison time"
 */
export function shortenUnit(effect: string): string {
  return `${effect.charAt(0).toUpperCase()}${effect.slice(1).replaceAll("_", " ")} time`;
}

/**
 * What a use gives back at once, read for the headline: health from its heal, else stamina from its stamina restore.
 *
 * @param record The use's record.
 * @returns The amount and its unit, or null for a use that restores nothing.
 */
function restoreOf(record: Ability): { readonly amount: number; readonly unit: string } | null {
  if (record.heal !== null) {
    return { amount: record.heal.amount, unit: say("bag.card.health") };
  }
  return record.restoreStamina === null ? null : { amount: record.restoreStamina.amount, unit: say("bag.card.stamina") };
}

/**
 * The card's headline for a use: what it restores, "30" and "Health" or "Stamina", else the first effect it shortens,
 * "-15%" and "Poison time".
 *
 * @param record The use's record, or undefined for an item with no use.
 * @returns The headline, or null for neither.
 */
function headlineOf(record: Ability | undefined): CardHeadline | null {
  if (record === undefined) {
    return null;
  }
  const restore = restoreOf(record);
  if (restore !== null) {
    return { value: String(restore.amount), unit: restore.unit };
  }
  const shorten = record.shortens[0];
  return shorten === undefined ? null : { value: `-${shorten.percent}%`, unit: shortenUnit(shorten.effect) };
}

/**
 * Each rarity's word on the card's band (ADR-125 decision 1.1).
 */
export const RARITY_WORDS: Readonly<Record<ItemRarity, string>> = { common: "Common", uncommon: "Uncommon", rare: "Rare", epic: "Epic", legendary: "Legendary", relic: "Relic" };

/**
 * How many sparks the picture plate draws for each rarity (ADR-125 decision 5.2): the design's two for Very Rare,
 * which items call Epic, and five for Legendary; five for a Relic, proposed, since the design draws no Relic.
 */
export const RARITY_SPARKS: Readonly<Record<ItemRarity, number>> = { common: 0, uncommon: 0, rare: 0, epic: 2, legendary: 5, relic: 5 };

/**
 * One stat row: its 8 px glyph, its label, its value, and whether the value is drawn in the danger colour (an amount
 * the rules lessen now).
 */
export interface CardStat {
  readonly glyph: GlyphName;
  readonly label: string;
  readonly value: string;
  readonly harm: boolean;
}

/**
 * The card's headline: the one big number and its unit.
 */
export interface CardHeadline {
  readonly value: string;
  readonly unit: string;
}

/**
 * An item card's content: the rarity, its band's word and filled pips (null for a Relic, which is off the ladder and
 * draws no pips), the sparks on its picture, the name with a stack's count, the type line, the headline or null, the
 * stat rows and the description.
 */
export interface ItemCardContent {
  readonly rarity: ItemRarity;
  readonly rarityWord: string;
  readonly pips: number | null;
  readonly sparks: number;
  readonly name: string;
  readonly typeLine: string;
  readonly headline: CardHeadline | null;
  readonly stats: readonly CardStat[];
  readonly description: string;
}

/**
 * The item card of a stack (ADR-125 decision 5.2): for a consumable whose use heals or restores stamina, the amount as
 * the headline, "30" and "Health" or "Stamina", and the row "Against players" with the amount the rules' own
 * `consumableHeal` gives in combat with a player, drawn in the danger colour while that applies; for one whose use
 * only shortens an effect, "-15%" and "Poison time" with no "Against players" row (ADR-091's note for P2.8-T57, item 7); "Cooldown" with its time when the use has one; the
 * description, its tokens filled (`itemDescription`, ADR-091's amendment of 2026-10-09), in the flavour line's place.
 *
 * @param rules The page's rules.
 * @param id The item's rules id.
 * @param quantity The stack's count.
 * @param halved Whether a consumable's heal is halved now: in combat with a player (`InPvpCombat`).
 * @returns The card's content, or null for an id the rules lack.
 * @example
 * itemCard(rules, "minor_healing_potion", 5, false)?.headline;
 * // { value: "30", unit: "Health" }
 */
export function itemCard(rules: Rules, id: string, quantity: number, halved: boolean): ItemCardContent | null {
  const item = rules.items.get(id);
  if (item === undefined) {
    return null;
  }
  const record = item.use === null ? undefined : rules.abilities.get(item.use);
  const restore = record === undefined ? null : restoreOf(record);
  const stats: CardStat[] = [];
  if (restore !== null) {
    stats.push({ glyph: "human-small", label: say("bag.card.againstPlayers"), value: String(consumableHeal(restore.amount, true)), harm: halved });
  }
  if (record !== undefined && record.cooldownTicks > 0) {
    stats.push({ glyph: "clock", label: say("bag.card.cooldown"), value: durationText(record.cooldownTicks), harm: false });
  }
  return {
    rarity: item.rarity,
    rarityWord: RARITY_WORDS[item.rarity],
    pips: item.rarity === "relic" ? null : RARITY_PIPS[item.rarity],
    sparks: RARITY_SPARKS[item.rarity],
    name: menuHeading(item.name, quantity),
    typeLine: say(`bag.kinds.${item.kind}`),
    headline: headlineOf(record),
    stats,
    description: itemDescription(rules, item),
  };
}
